/**
 * Feature Flag Hooks
 *
 * React hooks for using feature flags in components
 */

import { useWorkspacePreferences } from '@/lib/hooks/use-workspace-preferences';
import { isLovablePreview } from '@/lib/utils/environment';
import { FeatureFlag } from './types';
import { getFeatureFlagValue } from './utils';

/**
 * Hook to check if a feature flag is enabled
 *
 * @param flag - The feature flag to check
 * @returns boolean indicating if the feature is enabled
 *
 * @example
 * ```tsx
 * import { useFeatureFlag } from '@/lib/feature-flags';
 * import { FEATURE_FLAGS } from '@/lib/feature-flags/registry';
 *
 * function MyComponent() {
 *   const isAiColumnsEnabled = useFeatureFlag(FEATURE_FLAGS.AI_COLUMNS);
 *
 *   if (!isAiColumnsEnabled) {
 *     return null;
 *   }
 *
 *   return <AiColumnsFeature />;
 * }
 * ```
 */
export const useFeatureFlag = (flag: FeatureFlag): boolean => {
  const { workspacePreferences, loading } = useWorkspacePreferences();

  // In Lovable Preview mode, enable all features
  if (isLovablePreview()) {
    return true;
  }

  // While loading, return default value to avoid flashing
  if (loading) {
    return flag.defaultValue;
  }

  return getFeatureFlagValue(flag, workspacePreferences);
};

/**
 * Hook to check multiple feature flags at once
 *
 * @param flags - Array of feature flags to check
 * @returns Object mapping flag keys to boolean values
 *
 * @example
 * ```tsx
 * import { useFeatureFlags } from '@/lib/feature-flags';
 * import { FEATURE_FLAGS } from '@/lib/feature-flags/registry';
 *
 * function MyComponent() {
 *   const features = useFeatureFlags([
 *     FEATURE_FLAGS.AI_COLUMNS,
 *     FEATURE_FLAGS.BUILD_COMPANY_LIST,
 *   ]);
 *
 *   return (
 *     <div>
 *       {features['ai-columns'] && <AiColumnsFeature />}
 *       {features['build-company-list'] && <CompanyListFeature />}
 *     </div>
 *   );
 * }
 * ```
 */
export const useFeatureFlags = (flags: FeatureFlag[]): Record<string, boolean> => {
  const { workspacePreferences, loading } = useWorkspacePreferences();

  // In Lovable Preview mode, enable all features
  if (isLovablePreview()) {
    return flags.reduce((acc, flag) => {
      acc[flag.key] = true;
      return acc;
    }, {} as Record<string, boolean>);
  }

  return flags.reduce((acc, flag) => {
    // While loading, return default values
    if (loading) {
      acc[flag.key] = flag.defaultValue;
    } else {
      acc[flag.key] = getFeatureFlagValue(flag, workspacePreferences);
    }
    return acc;
  }, {} as Record<string, boolean>);
};

/**
 * Hook to get all feature flags and their values
 *
 * @returns Object mapping all flag keys to boolean values
 *
 * @example
 * ```tsx
 * import { useAllFeatureFlags } from '@/lib/feature-flags';
 *
 * function FeatureFlagsDebugPanel() {
 *   const allFlags = useAllFeatureFlags();
 *
 *   return (
 *     <ul>
 *       {Object.entries(allFlags).map(([key, enabled]) => (
 *         <li key={key}>
 *           {key}: {enabled ? 'enabled' : 'disabled'}
 *         </li>
 *       ))}
 *     </ul>
 *   );
 * }
 * ```
 */
export const useAllFeatureFlags = (): Record<string, boolean> => {
  const { workspacePreferences, loading } = useWorkspacePreferences();

  if (!workspacePreferences || loading) {
    return {};
  }

  try {
    const preferences = JSON.parse(workspacePreferences.preferences || '{}');
    return preferences;
  } catch (e) {
    console.error('Failed to parse workspace preferences:', e);
    return {};
  }
};
